import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Badge from "@/v2-backoffice-client/components/badge"
import UsageGeoAreas from "@/v2-backoffice-client/usage/UsageGeoAreas"
import UsageActivityChart from "@/v2-backoffice-client/usage/UsageActivityChart"
import UsageAppFilterChips from "@/v2-backoffice-client/usage/UsageAppFilterChips"
import UsageProductChips from "@/v2-backoffice-client/usage/UsageProductChips"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import UsageReportBarList from "@/v2-backoffice-client/usage/report/UsageReportBarList"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageReportKpi from "@/v2-backoffice-client/usage/report/UsageReportKpi"
import UsageReportNotes from "@/v2-backoffice-client/usage/report/UsageReportNotes"
import UsageReportSection from "@/v2-backoffice-client/usage/report/UsageReportSection"
import {
  formatAppArea,
  formatAppCode,
  GEO_AREAS_TOOLTIP,
  INTEREST_AREAS,
  USAGE_REPORT_NOTES,
  formatMunicipalityName,
} from "@/v2-backoffice-client/usage/usage.constants"
import {
  deriveAreaBreakdown,
  filterAreaDaysByApp,
  filterConsoleAreaDays,
  getAreaDayApps,
} from "@/v2-backoffice-client/usage/usage.utils"
import { useUsageScopeData } from "@/v2-backoffice-client/usage/scope/usage.scope.data"
import {
  activeApps,
  countConsoleActiveSeats,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import { list } from "@/v2-backoffice-client/usage/usage.text"
import { usageKpiGridClass } from "@/v2-backoffice-client/usage/report/usage.report.utils"

export type UsageFusionReportProps = {
  /** Scope to a team's active members. Exactly one scope prop is set. */
  tenantId?: number
  /** Scope to a customer's active users. */
  customerId?: number
  /** The customer's or team's name, for the title. */
  subject: string
}

// The Fusion tab of a customer's or team's Activity: what the people do in
// the console — the use cases they work in and the ones nobody has
// touched, the municipalities their work concentrates in, and activity day
// by day with the trend line. Reads the same scope data as the Overview.
function UsageFusionReport(props: UsageFusionReportProps) {
  const { tenantId, customerId, subject } = props

  const { members, summary, areaDays, appMix, loading, error } =
    useUsageScopeData({ tenantId, customerId })
  const [appFilter, setAppFilter] = useState<string | null>(null)

  // This tab is the consoles' alone: an MCP-only scope must not read as
  // Fusion work, and the use cases must not count MCP tool calls.
  const consoleAreaDays = filterConsoleAreaDays(areaDays)
  const apps = getAreaDayApps(consoleAreaDays)
  const filteredAreaDays = filterAreaDaysByApp(consoleAreaDays, appFilter)
  // Every used area, not the top few: the untouched list is its complement.
  const areaBreakdown = deriveAreaBreakdown(
    consoleAreaDays,
    undefined,
    Infinity,
  )
  const usedAreas = new Set(areaBreakdown.map((area) => area.area))
  const untouchedAreas = INTEREST_AREAS.filter((area) => !usedAreas.has(area))
  const consoleSeats = countConsoleActiveSeats(members)
  const topArea = areaBreakdown[0]
  const otherProducts = activeApps(appMix).filter((app) => !apps.includes(app))

  if (loading && areaDays.length === 0 && summary.seatCount === 0) {
    return <p className="text-sm text-muted-foreground">Loading usage…</p>
  }

  if (consoleAreaDays.length === 0) {
    return (
      <UsageReportSection
        eyebrow="Fusion"
        title={`What ${subject} does in Fusion`}
        lead="The console work of the last 28 days: which use cases, where in the country, and how it spreads over the days."
      >
        <ErrorMessage error={error} />
        <p className="usage-report-empty">
          Nobody at {subject} has worked in Fusion in the last 90 days.
          {otherProducts.length > 0
            ? ` Their activity is in ${list(otherProducts.map(formatAppCode))}: ${
                tenantId != null
                  ? "the customer's Activity tab has it"
                  : `the ${otherProducts.length === 1 ? "tab" : "tabs"} next to this one ${otherProducts.length === 1 ? "has" : "have"} it`
              }.`
            : ""}
        </p>
      </UsageReportSection>
    )
  }

  return (
    <UsageReportSection
      eyebrow="Fusion"
      title={`What ${subject} does in Fusion`}
      lead="The console work of the last 28 days: which use cases, where in the country, and how it spreads over the days. Activity is weighted by action — exports, reports and saves count more than clicks."
      aside={<UsageProductChips apps={apps} />}
    >
      <ErrorMessage error={error} />

      <div className={usageKpiGridClass(4)}>
        <UsageReportKpi
          label="Seats in use"
          value={
            <>
              {consoleSeats}
              <span className="text-sm font-normal text-muted-foreground">
                of {summary.seatCount}
              </span>
            </>
          }
          sub="in Fusion in the last 28 days"
        />
        <UsageReportKpi
          label="Use cases"
          value={areaBreakdown.length}
          sub={
            topArea
              ? `mostly ${formatAppArea(topArea.area)}`
              : "no use-case activity in the last 28 days"
          }
        />
        <UsageReportKpi
          label="Untouched"
          value={untouchedAreas.length}
          sub="use cases nobody here has opened"
        />
        <UsageReportKpi
          label="Municipalities"
          value={summary.topGeoAreas.length}
          sub={
            summary.topGeoAreas[0]
              ? `most work in ${formatMunicipalityName(summary.topGeoAreas[0].Name)}`
              : "no located work yet"
          }
        />
      </div>

      <div className="usage-report-grid usage-report-grid--two">
        <UsageReportCard
          title="What they use"
          caption="Weighted activity per use case in the last 28 days, with the trend on the four weeks before. Tailor the pitch to these."
        >
          <UsageReportBarList
            rows={areaBreakdown.map((area) => ({
              key: area.area,
              label: formatAppArea(area.area),
              value: Math.round(area.current),
              extra: (
                <UsageTrendBadge
                  trendPct={area.trendPct}
                  className="w-16 justify-center"
                />
              ),
            }))}
            labelWidth="w-44"
            emptyText="No use-case activity in the last 28 days."
          />
        </UsageReportCard>

        <UsageReportCard
          title="Where they work"
          info={GEO_AREAS_TOOLTIP}
          caption="Municipalities where their located work of the last 28 days concentrates — the markets to anchor reports and prospecting on."
        >
          {summary.topGeoAreas.length > 0 ? (
            <UsageGeoAreas areas={summary.topGeoAreas} />
          ) : (
            <p className="text-xs text-muted-foreground">
              No located work yet. Municipalities appear once tracked work
              carries a property or a map position.
            </p>
          )}
        </UsageReportCard>

        {untouchedAreas.length > 0 && (
          <UsageReportCard
            title="Not opened yet"
            caption="Use cases nobody here has touched in the last 28 days — training or expansion openings."
            wide
          >
            <div className="usage-report-chips">
              {untouchedAreas.map((area) => (
                <Badge
                  key={area}
                  variant="outline"
                  size="sm"
                  className="font-normal text-muted-foreground"
                >
                  {formatAppArea(area)}
                </Badge>
              ))}
            </div>
          </UsageReportCard>
        )}

        {/* The chart brings its own header row (title, legend, range
            switch), so it sits in the grid without a card; the product chips
            above it filter what it shows. */}
        <div className="col-span-full flex flex-col gap-2">
          <UsageAppFilterChips
            apps={apps}
            value={appFilter}
            onChange={setAppFilter}
            className="self-end"
          />
          <UsageActivityChart areaDays={filteredAreaDays} />
        </div>

        <UsageReportCard wide>
          <UsageReportNotes notes={USAGE_REPORT_NOTES} />
        </UsageReportCard>
      </div>
    </UsageReportSection>
  )
}

export default UsageFusionReport
